import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FieldArray, Formik, Form } from "formik"
import * as Yup from "yup"
import { isEqual } from "lodash"
import { TableHead, TableBody, TableCell, Table, TableRow, Select, MenuItem, Checkbox } from "@mui/material"

import ExecutionsStatus from "./ExecutionsStatus"
import FilterRowSelectAndAutocomplete from "../../components/form/FilterRowSelectAndAutocomplete"
import { formatProductionStepsHoursEditInitialValues, PRODUCTION_STEP_EXECUTION_STATUSES, getPSEsHoursEditUniqueOptions, applyPSEsHoursEditFilters } from "../../utils/productionStepExecution"
import { getProductionStepsExecutionsSelector } from "../../reducers/Production/ProductionSteps"
import { saveProductionStepsExecutionsEditedHours, showProductionStepsSupervision } from "../../actions/ProductionSteps/ProductionStepExecutions"
import PSEHoursInput from "./preview/PSEHoursInput"

const formId = "productionStepsHoursEdit"

const tableHeaders = [
  { key: "theoreticalTime", label: "Heures théoriques" },
  { key: "name", label: "Nom de l'étape" },
  { key: "transformationType", label: "Type d'étape" },
  { key: "machines", label: "Machine" },
  { key: "section", label: "Section" },
  { key: "recipe", label: "Recette concernée" },
  { key: "status", label: "État de l'étape" },
]

const filterFields = [
  { key: "name", placeholder: "Nom de l'étape" },
  { key: "transformationType", placeholder: "Type d'étape", multiple: true },
  { key: "machines", placeholder: "Machine", isMachines: true, multiple: true },
  { key: "section", placeholder: "Section" },
  { key: "recipe", placeholder: "Recette concernée" },
  { key: "status", placeholder: "État de l'étape", isStatus: true },
]

const productionStepExecutionsHoursValidationSchema = Yup.object().shape({
  productionStepsExecutions: Yup.array().of(
    Yup.object().shape({
      theoreticalStartTime: Yup.string().validPEPSETime("L'heure doit être au format hh:mm"),
      theoreticalEndTime: Yup.string().validPEPSETime("L'heure doit être au format hh:mm"),
      machine: Yup.string(),
    })
  )
})

const BorderCell = () => <TableCell style={{ width: "56px" }} />

const ProductionStepsHoursEdit = () => {
  const dispatch = useDispatch()

  const productionStepExecutions = useSelector(getProductionStepsExecutionsSelector)
  const [productionStepExecutionsInitialValues, setProductionStepExecutionsInitialValues] = useState([])
  const [filters, setFilters] = useState({})

  useEffect(() => {
    setProductionStepExecutionsInitialValues(formatProductionStepsHoursEditInitialValues(productionStepExecutions))
  }, [productionStepExecutions])

  const handleSubmit = async (values) => {
    const updated = values.productionStepsExecutions
      .filter(pse => !isEqual(pse, productionStepExecutionsInitialValues.productionStepsExecutions.find(productionStepsExecution => productionStepsExecution.objectId === pse.objectId)))
      .map(({ objectId, theoreticalStartTime, theoreticalEndTime, machines }) => ({ objectId, theoreticalStartTime, theoreticalEndTime, machines }))

    if (updated.length) await dispatch(saveProductionStepsExecutionsEditedHours(updated))
    dispatch(showProductionStepsSupervision())
  }

  const renderStatus = (pse) => {
    const statusName = PRODUCTION_STEP_EXECUTION_STATUSES.find(({ value }) => value === pse.status)?.name
    return <ExecutionsStatus statusValue={pse.status} statusName={statusName} />
  }

  return (
    <Formik
      initialValues={productionStepExecutionsInitialValues}
      validationSchema={productionStepExecutionsHoursValidationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, touched, handleChange }) => (
        <Form id={formId}>
          <Table sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <BorderCell />
                {tableHeaders.map(({ key, label }) => <TableCell key={key}>{label}</TableCell>)}
                <BorderCell />
              </TableRow>
              <FilterRowSelectAndAutocomplete
                tableHeaders={tableHeaders}
                filterFields={filterFields}
                filters={filters}
                setFilters={setFilters}
                values={values}
                getUniqueOptions={(values, field, isMachines) =>
                  getPSEsHoursEditUniqueOptions(values, field, isMachines)
                }
              />
            </TableHead>
            <TableBody>
              <FieldArray name="productionStepsExecutions">
                {() => (applyPSEsHoursEditFilters(values,filters,filterFields,productionStepExecutions, PRODUCTION_STEP_EXECUTION_STATUSES) || []).map((PSE) => {
                  const originalIndex = values.productionStepsExecutions.findIndex(step => step.objectId === PSE.objectId)
                  return (
                    <TableRow key={PSE.objectId}>
                      <BorderCell />
                      <TableCell sx={{ py: "14px" }}>
                        <PSEHoursInput
                          startTime={{
                            name: `productionStepsExecutions.${originalIndex}.theoreticalStartTime`,
                            value: PSE.theoreticalStartTime,
                            error: touched?.productionStepsExecutions?.[originalIndex]?.theoreticalStartTime,
                          }}
                          endTime={{
                            name: `productionStepsExecutions.${originalIndex}.theoreticalEndTime`,
                            value: PSE.theoreticalEndTime,
                            error: touched?.productionStepsExecutions?.[originalIndex]?.theoreticalEndTime,
                          }}
                          onChange={handleChange}
                        />
                      </TableCell>
                      <TableCell>{PSE.name}</TableCell>
                      <TableCell>{PSE.transformationType}</TableCell>
                      <TableCell sx={{ py: "14px" }}>
                        {productionStepExecutions[originalIndex]?.machinesOptions?.length > 0 && (
                          <Select
                            multiple
                            name={`productionStepsExecutions.${originalIndex}.machines`}
                            value={PSE.machines}
                            onChange={handleChange}
                            variant="standard"
                            sx={{ width: "100%", "& .MuiSelect-select": { display: "flex", flexWrap: "wrap" } }}
                            renderValue={(selected) => selected.map(id => productionStepExecutions[originalIndex].machinesOptions.find(m => m.objectId === id)?.name).join(", ")}
                          >
                            {productionStepExecutions[originalIndex].machinesOptions.map(machine => (
                              <MenuItem key={machine.objectId} value={machine.objectId}>
                                <Checkbox checked={PSE.machines.includes(machine.objectId)} />
                                {machine.name}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>{PSE.section}</TableCell>
                      <TableCell>{PSE.recipe}</TableCell>
                      <TableCell sx={{ py: "14px" }}>{renderStatus(PSE)}</TableCell>
                      <BorderCell />
                    </TableRow>
                  )
                })}
              </FieldArray>
            </TableBody>
          </Table>
        </Form>
      )}
    </Formik>
  )
}

export default ProductionStepsHoursEdit